/**
 * Persistance des traces et des caches réseau.
 *
 * L'application Android garde le fichier GPX d'origine intact et stocke les
 * annotations à côté ; on reprend ce principe : le contenu du fichier importé
 * est conservé tel quel, et tout ce que l'utilisateur ajoute vit dans un
 * enregistrement séparé.
 *
 * IndexedDB plutôt que `localStorage` : une trace de 200 000 points pèse
 * plusieurs mégaoctets, bien au-delà du quota des clés/valeurs.
 */

import type { CustomMarker, GpxSourceType, WayInOut } from './types'

const DB_NAME = 'velogpx'
const DB_VERSION = 1

const STORE_TRACKS = 'tracks'
const STORE_FILES = 'files'
const STORE_CACHE = 'cache'

/**
 * Préfixe des caches du service worker appartenant à l'application.
 *
 * Le domaine peut en héberger d'autres — une autre application dans un
 * sous-répertoire, un outil de mesure : la purge ne touche que les siens.
 */
const CACHE_PREFIX = 'velogpx-'

/**
 * Cache de la coque, épargné par la purge.
 *
 * Le vider ferait perdre le démarrage hors ligne jusqu'à la prochaine visite
 * en ligne — beaucoup pour quelques kilo-octets, et l'utilisateur qui vide son
 * cache veut de la place, pas une application qui ne s'ouvre plus en zone
 * blanche. Le nom porte l'empreinte de la compilation
 * (`velogpx-shell-<empreinte>`), d'où la comparaison par préfixe : elle change
 * à chaque mise en ligne, et une comparaison exacte cesserait d'épargner la
 * coque dès le déploiement suivant.
 */
const SHELL_CACHE_PREFIX = `${CACHE_PREFIX}shell`

/**
 * Préfixe des caches de tuiles du service worker.
 *
 * Il porte une version fixe dans `public/sw.js` (`velogpx-tiles-v1`), à la
 * différence de la coque : les tuiles ne doivent pas partir à chaque mise en
 * ligne. La comparaison se fait tout de même sur le préfixe, pour survivre au
 * jour où cette version bougera.
 */
const TILE_CACHE_PREFIX = `${CACHE_PREFIX}tiles`

/** Ce dont la purge a besoin de `caches`, réduit pour être éprouvable. */
export interface CacheStorageLike {
  keys(): Promise<string[]>
  delete(name: string): Promise<boolean>
}

/** Ce qu'il faut en plus pour compter les tuiles retenues. */
export interface CountableCacheStorage extends CacheStorageLike {
  open(name: string): Promise<{ keys(): Promise<readonly unknown[]> }>
}

/** Les quatre natures de cache que la section « Stockage » distingue. */
export type CacheKind = 'tiles' | 'routes' | 'radars' | 'elevations'

/** Nombre d'entrées retenues, par nature. */
export type CacheCounts = Readonly<Record<CacheKind, number>>

/**
 * Nature d'une entrée du magasin de cache, d'après son préfixe de clé.
 *
 * C'est la convention de nommage qui les sépare — `route_`, `way_`,
 * `radars_`, `ele_` —, faute d'un magasin par nature : IndexedDB en
 * demanderait autant de migrations. Les détours (`way_`) comptent avec les
 * itinéraires : ce sont des réponses d'OSRM, obtenues et perdues de la même
 * façon.
 *
 * Rend `null` pour une clé d'une autre nature — une entrée écrite par une
 * version plus récente, par exemple : mieux vaut ne pas la compter que de la
 * compter au mauvais endroit.
 */
export function cacheKindOf(key: string): Exclude<CacheKind, 'tiles'> | null {
  if (key.startsWith('route_') || key.startsWith('way_')) return 'routes'
  if (key.startsWith('radars_')) return 'radars'
  if (key.startsWith('ele_')) return 'elevations'
  return null
}

/**
 * Compte les tuiles retenues par le service worker.
 *
 * Ce qui se compte est le **nombre d'entrées**, jamais leur poids : peser
 * demanderait de relire chacune des 3 000 réponses possibles, et la section
 * ne s'ouvrirait plus. Voir le tableau des écarts du `README.md`.
 */
export async function countTileCache(
  store: CountableCacheStorage | null,
): Promise<number> {
  if (!store) return 0

  const names = (await store.keys().catch(() => [])).filter((name) =>
    name.startsWith(TILE_CACHE_PREFIX),
  )

  const counts = await Promise.all(
    names.map((name) =>
      store
        .open(name)
        .then((cache) => cache.keys())
        .then((keys) => keys.length)
        .catch(() => 0),
    ),
  )

  return counts.reduce((total, count) => total + count, 0)
}

/**
 * Supprime les caches du service worker, la coque exceptée.
 *
 * Les noms sont fixés dans `public/sw.js` : c'est la convention de nommage qui
 * les relie, pas une constante partagée — le service worker ne peut rien
 * importer du code applicatif.
 */
export async function clearServiceWorkerCaches(
  store: CacheStorageLike | null,
): Promise<void> {
  if (!store) return

  const names = await store.keys().catch(() => [])

  await Promise.all(
    names
      .filter(
        (name) =>
          name.startsWith(CACHE_PREFIX) && !name.startsWith(SHELL_CACHE_PREFIX),
      )
      // Un cache non supprimé ne coûte que de la place : l'échec de l'un ne
      // doit pas emporter les autres.
      .map((name) => store.delete(name).catch(() => false)),
  )
}

/** Enregistrement d'une trace : tout sauf la géométrie, qui vit dans `files`. */
export interface StoredTrack {
  readonly id: string
  /** Rang d'affichage ; le plus petit passe devant. */
  order: number
  name: string
  color: string
  visible: boolean
  realStartDistance: number
  kmMarkerInterval: number
  kmMarkersFromFictiveStart: boolean
  isRouted: boolean
  routingProfile: string
  sourceType: GpxSourceType
  customMarkers: CustomMarker[]
  wayInOuts: WayInOut[]
  /** Index du `<trk>` d'origine dans le fichier source. */
  fileTrackIndex: number
  includeFileTracks: boolean
  includeFileWaypoints: boolean
  /**
   * Affichage des points d'intérêt sur la carte — **hérité, plus écrit**.
   *
   * Il doublait `includeFileWaypoints` d'un second état, sans équivalent
   * Android : les points restaient alors dans la trace, donc dans un fichier
   * exporté, tout en étant invisibles. Les enregistrements qui le portent sont
   * relus par `restoredFileWaypoints`, qui les ramène à l'unique drapeau.
   */
  showWaypoints?: boolean
  /** Clé de l'entrée `files` contenant le GPX d'origine. */
  fileKey: string
  importedAt: number
}

export interface CacheEntry<T = unknown> {
  readonly key: string
  readonly value: T
  readonly storedAt: number
}

/**
 * Identifiant unique sans exigence de contexte sécurisé.
 *
 * `crypto.randomUUID` ne convient pas ici : il n'est exposé qu'en contexte
 * sécurisé, exactement comme le `crypto.subtle` dont on cherche à se passer.
 * Deux sources indépendantes suffisent — l'horloge écarte deux appels
 * successifs, le tirage écarte deux onglets sur la même milliseconde.
 */
function randomFileKey(): string {
  const now = Date.now().toString(36)
  const draw = Math.floor(Math.random() * 0xffffffff).toString(36)
  return `${now}-${draw}`
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = () => {
      const db = request.result

      if (!db.objectStoreNames.contains(STORE_TRACKS)) {
        db.createObjectStore(STORE_TRACKS, { keyPath: 'id' })
      }
      if (!db.objectStoreNames.contains(STORE_FILES)) {
        db.createObjectStore(STORE_FILES)
      }
      if (!db.objectStoreNames.contains(STORE_CACHE)) {
        db.createObjectStore(STORE_CACHE, { keyPath: 'key' })
      }
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () =>
      reject(request.error ?? new Error('Ouverture de la base impossible.'))
  })
}

function runTransaction<T>(
  db: IDBDatabase,
  storeNames: string | string[],
  mode: IDBTransactionMode,
  work: (tx: IDBTransaction) => Promise<T> | T,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeNames, mode)
    let result: T

    tx.oncomplete = () => resolve(result)
    tx.onerror = () => reject(tx.error ?? new Error('Transaction échouée.'))
    tx.onabort = () => reject(tx.error ?? new Error('Transaction annulée.'))

    Promise.resolve(work(tx)).then(
      (value) => {
        result = value
      },
      (error: unknown) => {
        tx.abort()
        reject(error)
      },
    )
  })
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('Requête échouée.'))
  })
}

/**
 * Accès au stockage local.
 *
 * Toutes les méthodes tolèrent une base indisponible (navigation privée,
 * quota épuisé) : l'application reste utilisable pour la session en cours,
 * seule la persistance est perdue.
 */
export class Storage {
  #db: IDBDatabase | null = null
  #openPromise: Promise<IDBDatabase | null> | null = null

  async #database(): Promise<IDBDatabase | null> {
    if (this.#db) return this.#db

    this.#openPromise ??= openDatabase().then(
      (db) => {
        this.#db = db
        return db
      },
      (error: unknown) => {
        console.warn('[VeloGPX] Stockage local indisponible', error)
        return null
      },
    )

    return this.#openPromise
  }

  get available(): boolean {
    return this.#db !== null
  }

  /* --- Traces --------------------------------------------------------- */

  async listTracks(): Promise<StoredTrack[]> {
    const db = await this.#database()
    if (!db) return []

    const tracks = await runTransaction(db, STORE_TRACKS, 'readonly', (tx) =>
      requestToPromise(
        tx.objectStore(STORE_TRACKS).getAll() as IDBRequest<StoredTrack[]>,
      ),
    ).catch(() => [] as StoredTrack[])

    return tracks.sort((a, b) => a.order - b.order)
  }

  async saveTrack(track: StoredTrack): Promise<void> {
    const db = await this.#database()
    if (!db) return

    await runTransaction(db, STORE_TRACKS, 'readwrite', (tx) =>
      requestToPromise(tx.objectStore(STORE_TRACKS).put(track)),
    ).catch((error: unknown) => {
      console.warn('[VeloGPX] Enregistrement de la trace impossible', error)
    })
  }

  async saveTracks(tracks: readonly StoredTrack[]): Promise<void> {
    const db = await this.#database()
    if (!db) return

    await runTransaction(db, STORE_TRACKS, 'readwrite', (tx) => {
      const store = tx.objectStore(STORE_TRACKS)
      for (const track of tracks) store.put(track)
    }).catch((error: unknown) => {
      console.warn('[VeloGPX] Enregistrement des traces impossible', error)
    })
  }

  /**
   * Clé de dépôt d'un GPX, dérivée de son **contenu**.
   *
   * Deux imports du même fichier — le mode d'emploi pour prendre une autre de
   * ses traces — retombent donc sur la même entrée au lieu d'en écrire une
   * seconde. Sur un GPX de course de plusieurs mégaoctets, la différence se
   * voit dans le quota du navigateur.
   *
   * Repli sur un identifiant aléatoire quand `crypto.subtle` manque : il n'est
   * exposé qu'en contexte sécurisé, et l'application se sert aussi en HTTP sur
   * le conteneur de test. On y perd le partage, jamais l'import.
   *
   * Ce repli ne passe pas par `crypto.randomUUID` : il est réservé au **même**
   * contexte sécurisé que `crypto.subtle`, et sur la branche où `crypto`
   * manque entièrement l'appel lève d'emblée. La porte de secours était donc
   * fermée par la serrure qu'elle devait contourner, et l'exception remontait
   * à `#importFiles`, qui affichait « ce fichier n'est pas un GPX ».
   */
  static async fileKeyFor(content: string): Promise<string> {
    if (typeof crypto === 'undefined' || !crypto.subtle) {
      return `gpx/${randomFileKey()}`
    }

    try {
      const bytes = new TextEncoder().encode(content)
      const digest = await crypto.subtle.digest('SHA-256', bytes)
      const hex = [...new Uint8Array(digest)]
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('')
      return `gpx/${hex}`
    } catch {
      return `gpx/${randomFileKey()}`
    }
  }

  /**
   * Supprime une trace, et le fichier GPX **si plus rien ne le référence**.
   *
   * Plusieurs traces peuvent partager une copie source : les `<trk>` d'un même
   * fichier, et depuis que la clé dérive du contenu, deux imports du même
   * fichier. Effacer la copie à la première suppression laissait les autres
   * sans tracé d'origine à relire, sans points d'intérêt à reprendre, et sans
   * fichier à rejouer à l'export.
   */

  async deleteTrack(
    trackId: string,
    fileKey: string,
    options: { keepFile?: boolean } = {},
  ): Promise<void> {
    const db = await this.#database()
    if (!db) return

    await runTransaction(
      db,
      [STORE_TRACKS, STORE_FILES],
      'readwrite',
      (tx) => {
        tx.objectStore(STORE_TRACKS).delete(trackId)
        if (options.keepFile !== true) {
          tx.objectStore(STORE_FILES).delete(fileKey)
        }
      },
    ).catch((error: unknown) => {
      console.warn('[VeloGPX] Suppression impossible', error)
    })
  }

  /* --- Fichiers GPX d'origine ----------------------------------------- */

  async putFile(key: string, content: string): Promise<void> {
    const db = await this.#database()
    if (!db) return

    await runTransaction(db, STORE_FILES, 'readwrite', (tx) =>
      requestToPromise(tx.objectStore(STORE_FILES).put(content, key)),
    ).catch((error: unknown) => {
      console.warn('[VeloGPX] Copie du fichier impossible', error)
    })
  }

  async getFile(key: string): Promise<string | null> {
    const db = await this.#database()
    if (!db) return null

    return runTransaction(db, STORE_FILES, 'readonly', (tx) =>
      requestToPromise(
        tx.objectStore(STORE_FILES).get(key) as IDBRequest<string | undefined>,
      ),
    )
      .then((value) => value ?? null)
      .catch(() => null)
  }

  /* --- Caches réseau --------------------------------------------------- */

  async getCache<T>(key: string, maxAgeMs?: number): Promise<T | null> {
    const db = await this.#database()
    if (!db) return null

    const entry = await runTransaction(db, STORE_CACHE, 'readonly', (tx) =>
      requestToPromise(
        tx.objectStore(STORE_CACHE).get(key) as IDBRequest<
          CacheEntry<T> | undefined
        >,
      ),
    ).catch(() => undefined)

    if (!entry) return null
    if (maxAgeMs !== undefined && Date.now() - entry.storedAt > maxAgeMs) {
      return null
    }
    return entry.value
  }

  /**
   * Écrit une entrée de cache, sans jamais lever.
   *
   * Rend **si l'écriture a réussi**. Presque tous les appelants s'en moquent —
   * un cache non écrit ne fait que provoquer un nouvel appel réseau. Un seul
   * doit le savoir : l'itinéraire d'une trace née des seuls points d'intérêt
   * ne vit **que** là, son fichier ne portant aucun `<trk>` à relire. Voir
   * `RouteController`.
   */
  async putCache<T>(key: string, value: T): Promise<boolean> {
    const db = await this.#database()
    if (!db) return false

    return runTransaction(db, STORE_CACHE, 'readwrite', (tx) =>
      requestToPromise(
        tx.objectStore(STORE_CACHE).put({ key, value, storedAt: Date.now() }),
      ),
    ).then(
      () => true,
      () => false,
    )
  }

  /** Retire une entrée de cache — géométrie d'un détour supprimé, par exemple. */
  async deleteCache(key: string): Promise<void> {
    const db = await this.#database()
    if (!db) return

    await runTransaction(db, STORE_CACHE, 'readwrite', (tx) =>
      requestToPromise(tx.objectStore(STORE_CACHE).delete(key)),
    ).catch(() => {
      // Une entrée non retirée sera écrasée au prochain calcul.
    })
  }

  /**
   * Vide les caches réseau : entrées IndexedDB **et** caches du service
   * worker.
   *
   * Les seconds sont l'essentiel du volume — les tuiles de carte montent à
   * 3 000 entrées —, et les oublier faisait un bouton qui semblait sans effet.
   * Android vide les deux au même endroit (`clearAmbientCache` en plus des
   * répertoires), c'est l'équivalent ici.
   */
  async clearCache(): Promise<void> {
    await clearServiceWorkerCaches(
      typeof caches === 'undefined' ? null : caches,
    )

    const db = await this.#database()
    if (!db) return

    await runTransaction(db, STORE_CACHE, 'readwrite', (tx) =>
      requestToPromise(tx.objectStore(STORE_CACHE).clear()),
    ).catch(() => {})
  }

  /**
   * Nombre d'entrées retenues, par nature de cache.
   *
   * Android affiche quatre poids ; le navigateur ne sait pas peser ses caches
   * sans les relire un par un, alors qu'il sait les compter tout de suite.
   * C'est ce qui rend la purge lisible sur-le-champ, là où
   * `estimateUsage` met plusieurs dizaines de secondes à bouger.
   */
  async cacheCounts(): Promise<CacheCounts> {
    const tiles = await countTileCache(
      typeof caches === 'undefined' ? null : (caches as CountableCacheStorage),
    )

    const counts = { tiles, routes: 0, radars: 0, elevations: 0 }

    const db = await this.#database()
    if (!db) return counts

    const keys = await runTransaction(db, STORE_CACHE, 'readonly', (tx) =>
      requestToPromise(
        tx.objectStore(STORE_CACHE).getAllKeys() as IDBRequest<IDBValidKey[]>,
      ),
    ).catch(() => [] as IDBValidKey[])

    for (const key of keys) {
      const kind = cacheKindOf(String(key))
      if (kind) counts[kind] += 1
    }

    return counts
  }

  /** Estimation de l'espace occupé, pour la section « Stockage ». */
  async estimateUsage(): Promise<number | null> {
    if (typeof navigator === 'undefined' || !navigator.storage?.estimate) {
      return null
    }
    try {
      const estimate = await navigator.storage.estimate()
      return estimate.usage ?? null
    } catch {
      return null
    }
  }
}
